<template>
  <div class="p-page-wrapper p-projects">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
        <p-search-input
          v-model="search"
          :items="searchSuggestionsComputed"
          @submit="searchProjects"
        ></p-search-input>
      </div>
      <div class="p-toolbar-item d-inline-flex align-center ml-6">
        <div class="d-flex">
          <v-checkbox
            v-model="onlyThisManager"
            :label="$t('project.onlyMyProjects')"
            class="mx-6 my-0"
            hide-details
            dense
            @change="onOnlyThisManagerChange"
          ></v-checkbox>
        </div>
      </div>
      <div class="p-toolbar-item p-right">
        <v-btn
          v-if="hasAccess()"
          class="p-btn-rounded mr-4"
          large
          rounded
          @click.stop="openEditDialog(null)"
        >
          {{ $t('project.createProject') }}
        </v-btn>
        <v-btn
          v-if="hasAccess()"
          color="primary"
          class="p-btn-text"
          text
          large
          @click="labelsPanel = true"
        >
          {{ $t('label.manageLabels') }}
          <v-icon class="ml-2">mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <v-row v-if="projects.length > 0">
      <v-col>
        <v-data-table
          :headers="tableHeaders"
          :items="projectsComputed"
          :options.sync="tableOptions"
          :server-items-length="projectsTotalCount"
          class="p-no-borders"
          disable-filtering
          hide-default-footer
          @page-count="pageCount = $event"
          @update:options="reloadProjects"
        >
          <template v-slot:[`item.title`]="{ item }">
            <span role="button" class="font-weight-medium d-block" @click="onProjectTitleClick(item)">{{ item.title }}</span>
          </template>
          <template v-slot:[`item.changed`]="{ item }">
            <v-tooltip v-if="item.changed" bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on">
                  <span v-if="item.changed" class="item-changed"></span>
                </v-btn>
              </template>
              {{ $t('project.table.changedTooltip') }}
            </v-tooltip>
          </template>
          <template v-slot:[`item.startUtc`]="{ item }">
            <span class="p-projects__date-string">{{ getTime(item.startUtc) }}</span>
          </template>
          <template v-slot:[`item.stopUtc`]="{ item }">
            <span class="p-projects__date-string">{{ getTime(item.stopUtc) }}</span>
          </template>
          <template v-slot:[`item.managers`]="{ item }">
            <p-user-item
              v-for="managerId in item.managers"
              :key="managerId"
              :value="userShortById(managerId)"
              avatar-class="mr-2"
              avatar-only
              you
            ></p-user-item>
          </template>
          <template v-slot:[`item.labels`]="{ item }">
            <div class="p-chips-wrapper">
              <v-chip
                v-for="labelId of item.labels"
                :key="labelId"
                small
                outlined
                label
              >
                {{ (projectLabelById(labelId) || {}).name }}
              </v-chip>
            </div>
          </template>
          <template v-slot:[`item.public`]="{ item }">
            <v-simple-checkbox
              v-model="item.editedIsPublic"
              :ripple="false"
              :disabled="!hasAccess()"
              color='primary'
              class="p-inline-checkbox"
              @click="hasAccess() ? setProjectIsPublic_(item) : null"
            />
          </template>
          <template v-slot:[`item.statusId`]="{ item }">
            <p-project-status-select
              v-model="item.editedStatusId"
              :disabled="!hasAccess()"
              class="p-inline-select"
              hide-details
              dense
              @input="setProjectStatusId_(item)"
            ></p-project-status-select>
          </template>
          <template v-slot:[`item.bookingStatusId`]="{ item }">
            <p-project-booking-status-select
              v-model="item.editedBookingStatusId"
              :disabled="!hasAccess()"
              class="p-inline-select"
              hide-details
              dense
              @input="setProjectBookingStatusId_(item)"
            ></p-project-booking-status-select>
          </template>
          <template v-if="hasAccess('read', 'budget')" v-slot:[`item.budget`]="{ item }">
            <div :set="budget = budgetTotal(item.id).projectTotal">
              <span v-bind:class="{ 'pGreen--text': budget > 0, 'pRed--text': budget < 0 }">
                <p-currency :value="budget"></p-currency>
              </span>
            </div>
          </template>
          <template v-slot:[`item.audience`]="{ item }">
            <span>{{ item.audience }}</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="p-table-row-actions p-visible-on-row-hover">
              <template v-if="hasAccess()">
                <p-btn-icon type="edit" @click="openEditDialog(item)"></p-btn-icon>
                <p-btn-icon type="delete" @click="openDeleteDialog(item)"></p-btn-icon>
              </template>
              <template v-else>
                <p-btn-icon type="view" @click="openEditDialog(item)"></p-btn-icon>
              </template>
            </div>
          </template>
        </v-data-table>

        <v-pagination
          v-model="tablePage"
          :length="pageCount"
          :total-visible="15"
          class="mt-5"
        ></v-pagination>
      </v-col>
    </v-row>

    <v-row v-else-if="projectsLoaded && !search">
        <v-col>
          <v-divider class="mb-16"></v-divider>
          <p-no-data
            img="project.png"
            :text="$t('project.noProjects')"
            :btn-text="$t('project.createProject')"
            :hide-btn="!hasAccess()"
            @btn-click="openEditDialog(null)"
          ></p-no-data>
        </v-col>
      </v-row>

    <project-edit-dialog
      :dialog.sync="editDialog"
      :project="editedProject"
    ></project-edit-dialog>

    <project-delete-dialog
      :dialog.sync="deleteDialog"
      :project="deletedProject"
    ></project-delete-dialog>

    <project-labels-panel
      :panel.sync="labelsPanel"
    ></project-labels-panel>

  </div>
</template>


<script>
import { mapState, mapGetters, mapActions } from 'vuex'

import moment from 'moment'

import PProjectStatusSelect from '@/components/PProjectStatusSelect'
import PProjectBookingStatusSelect from '@/components/PProjectBookingStatusSelect'
import PUserItem from '@/components/PUserItem'
import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PBtnIcon from '@/components/PBtnIcon'
import PCurrency from '@/components/PCurrency'

import ProjectEditDialog from './ProjectEditDialog'
import ProjectDeleteDialog from './ProjectDeleteDialog'
import ProjectLabelsPanel from './ProjectLabelsPanel'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import userSettingsMixin from '@/mixins/userSettings.mixin'
import objectMixin from '@/mixins/object.mixin'
import accessMixin from '@/mixins/access.mixin'

import eventBus from '@/eventBus'

export default {
  components: {
    PProjectStatusSelect,
    PProjectBookingStatusSelect,
    PUserItem,
    PSearchInput,
    PNoData,
    PBtnIcon,
    PCurrency,
    ProjectEditDialog,
    ProjectDeleteDialog,
    ProjectLabelsPanel
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    userSettingsMixin,
    objectMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      onlyThisManager: false,
      tableOptions: {
        page: 1,
        itemsPerPage: 20,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        groupDesc: []
      },
      pageCount: 0,
      editDialog: false,
      editedProject: null,
      deleteDialog: false,
      deletedProject: null,
      labelsPanel: false,
      projectsLoaded: false,
      searchSuggestionsComputed: [],
      tablePage: 1
    }
  },

  watch: {
    currentId () {
      // this.updateUserState()
    },

    onlyThisManager () {
      // this.updateUserState()
    },

    currentSeasonId () {
      this.reloadProjects(1)
    },

    tablePage (newTablePage) {
      this.tableOptions.page = newTablePage
      this.reloadProjects(newTablePage)
    }
  },

  computed: {
    ...mapState('project', ['projectsTotalCount', 'currentId']),
    ...mapGetters('project', ['projects', 'searchSuggestions', 'searchSuggestionsOnlyThisManager']),
    ...mapGetters('season', ['currentSeasonId']),
    ...mapGetters('projectLabel', ['projectLabelById']),
    ...mapGetters('user', ['userShortById']),
    ...mapGetters('scheme', ['schemes']),
    ...mapGetters('budget', ['budgetTotal']),
    ...mapGetters('userState', ['userState']),
    ...mapGetters('changes', ['isItemChanged']),

    projectsComputed () {
      return this.projects.map(project => {
        return {
          ...project,
          editedStatusId: project.statusId,
          editedBookingStatusId: project.bookingStatusId,
          editedIsPublic: project.public,
          changed: this.isItemChanged(project.id)
        }
      })
    },

    tableHeaders () {
      if (this.hasAccess('read', 'budget')) {
        return [
          { text: this.$t('project.table.title'), value: 'title' },
          { text: '', value: 'changed' },
          { text: this.$t('project.table.projectNumber'), value: 'projectNumber' },
          { text: this.$t('project.table.start'), value: 'startUtc' },
          { text: this.$t('project.table.stop'), value: 'stopUtc' },
          { text: this.$t('project.table.labels'), value: 'labels', sortable: false },
          { text: this.$t('project.table.managers'), value: 'managers', sortable: false },
          { text: this.$t('project.table.status'), value: 'statusId' },
          { text: this.$t('project.table.bookingStatus'), value: 'bookingStatusId' },
          { text: this.$t('project.table.public'), value: 'public' },
          { text: this.$t('project.table.budget'), value: 'budget', align: 'end', sortable: false },
          { text: this.$t('project.table.audienceSum'), value: 'audience', align: 'end', sortable: false },
          { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
        ]
      } else {
        return [
          { text: this.$t('project.table.title'), value: 'title' },
          { text: this.$t('project.table.projectNumber'), value: 'projectNumber' },
          { text: this.$t('project.table.start'), value: 'startUtc' },
          { text: this.$t('project.table.stop'), value: 'stopUtc' },
          { text: this.$t('project.table.labels'), value: 'labels', sortable: false },
          { text: this.$t('project.table.managers'), value: 'managers', sortable: false },
          { text: this.$t('project.table.status'), value: 'statusId' },
          { text: this.$t('project.table.bookingStatus'), value: 'bookingStatusId' },
          { text: this.$t('project.table.public'), value: 'public' },
          { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
        ]
      }
    }
  },

  methods: {
    ...mapActions('project', ['loadProjects', 'loadProjectById', 'setProjectIsPublic', 'setProjectStatusId', 'setProjectBookingStatusId', 'setCurrentProject']),
    ...mapActions('projectLabel', ['loadProjectLabels']),
    ...mapActions('season', ['loadSeasons']),
    ...mapActions('user', ['loadUsersShort', 'loadCurrentUserAndProjectClaims']),
    ...mapActions('scheme', ['loadSchemes']),
    ...mapActions('budget', ['loadBudgetTotal']),
    ...mapActions('userState', ['updateUserState']),
    ...mapActions('changes', ['removeFromChangedItems']),


    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        await this.searchProjects(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.reloadProjects()
      })
    },

    onProjectTitleClick (project) {
      this.setCurrentProject(project.id)
      this.loadCurrentUserAndProjectClaims(project.id)
      this.$router.push('events').catch(() => {})
    },

    async openEditDialog (project) {
      try {
        if (project) {
          this.removeFromChangedItems(project.id)
          this.startPageLoading()
          this.editedProject = await this.loadProjectById(project.id)
        } else {
          this.editedProject = null
        }
        this.editDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    async openDeleteDialog (project) {
      try {
        if (project) {
          this.startPageLoading()
          this.deletedProject = await this.loadProjectById(project.id)
        }
        this.deleteDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    async searchProjects (val) {
      this.search = val
      this.tableOptions.page = 1
      await this.reloadProjects()
    },

    async onOnlyThisManagerChange () {
      this.updateUserState({
        onlyThisManager: this.onlyThisManager
      })
      if (this.onlyThisManager) {
        this.searchSuggestionsComputed = this.searchSuggestionsOnlyThisManager
      } else {
        this.searchSuggestionsComputed = this.searchSuggestions
      }
      this.tableOptions.page = 1
      await this.reloadProjects()
    },

    async reloadProjects () {
      this.saveTableOptions()
      try {
        this.startPageLoading()
        const sortBy = this.tableOptions.sortBy.length ? this.tableOptions.sortBy[0] : null
        const sortDesc = this.tableOptions.sortDesc.length ? this.tableOptions.sortDesc[0] : false
        const onlyThisManager = this.onlyThisManager
        await this.loadProjects({
          skip: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
          take: this.tableOptions.itemsPerPage,
          search: this.search,
          sortBy,
          sortDesc,
          onlyThisManager
        })
        this.loadBudgetTotals()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    getTime (date) {
      return date ? moment(date).format('ll') : ''
    },

    async setProjectIsPublic_ (project) {
      try {
        this.showSnackbarProcessing()
        await this.setProjectIsPublic({ projectId: project.id, isPublic: project.editedIsPublic })
        this.showSnackbarSuccess(this.$t('saved'))
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
        project.editedIsPublic = project.public
      }
    },

    async setProjectStatusId_ (project) {
      try {
        this.showSnackbarProcessing()
        await this.setProjectStatusId({ projectId: project.id, statusId: project.editedStatusId })
        this.showSnackbarSuccess(this.$t('saved'))
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
        project.editedStatusId = project.statusId
      }
    },

    async setProjectBookingStatusId_ (project) {
      try {
        this.showSnackbarProcessing()
        await this.setProjectBookingStatusId({ projectId: project.id, bookingStatusId: project.editedBookingStatusId })
        this.showSnackbarSuccess(this.$t('saved'))
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
        project.editedBookingStatusId = project.bookingStatusId
      }
    },

    loadBudgetTotals () {
      if (!this.hasAccess('read', 'budget')) return
      for (const project of this.projects) {
        this.loadBudgetTotal(project.id)
      }
    },

    saveTableOptions () {
      if (this.tableOptions.sortBy && this.tableOptions.sortBy.length) {
        this.userSettings.projectsTableOptionsSortBy = this.tableOptions.sortBy[0]
      }
      if (this.tableOptions.sortDesc && this.tableOptions.sortDesc.length) {
        this.userSettings.projectsTableOptionsSortDesc = this.tableOptions.sortDesc[0]
      }
      this.setUserSettings()
    },

    restoreTableOptions () {
      this.tableOptions.sortBy = []
      if (this.userSettings?.projectsTableOptionsSortBy) {
        this.tableOptions.sortBy.push(this.userSettings?.projectsTableOptionsSortBy)
      }

      this.tableOptions.sortDesc = []
      if (this.userSettings?.projectsTableOptionsSortDesc) {
        this.tableOptions.sortDesc.push(this.userSettings?.projectsTableOptionsSortDesc)
      }
    }
  },


  async created () {
    this.initEventBus()
    this.onlyThisManager = this.userState?.onlyThisManager ?? false
    this.searchSuggestionsComputed = this.searchSuggestions
    this.setPageTitle(this.$t('nav.projects'))
    try {
      this.startPageLoading()
      await Promise.all([
        this.loadProjectLabels(),
        this.loadUsersShort(),
        this.loadSeasons(),
        this.loadSchemes()
      ])
      await this.loadProjects({ take: this.tableOptions.itemsPerPage })
      this.projectsLoaded = true
      this.loadBudgetTotals()
      this.restoreTableOptions()
      if (this.$router.currentRoute.params?.goto) {
        await this.searchProjects(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
